import { useEffect, useState } from 'react';

/**
 * Registre des illustrations d'en-tete, indexe par la cle `illustration`
 * du theme d'un serveur.
 *
 * Chaque image est l'illustration de la maquette, exportee telle quelle
 * dans `client/public/illustrations/`. Le gabarit est toujours le meme :
 * 1206 px de large, bord bas bombe compris, cadre en `object-fit: cover`.
 *
 * Pour ouvrir une ville : ajouter son theme cote serveur, deposer ses
 * illustrations dans ce dossier, puis les enregistrer ici.
 */
const HEADERS: Record<string, string> = {
  marrakech: '/illustrations/marrakech-header.png',
  londres: '/illustrations/londres-header.png',
  barcelone: '/illustrations/barcelone-header.png',
  berlin: '/illustrations/berlin-header.png',
  paris: '/illustrations/paris-header.png',
  /* En JPEG, tel qu'il a ete livre : converti en PNG, il pesait le double,
     et chaque telephone le telecharge avec la mise a jour. */
  essec: '/illustrations/essec-header.jpg',
  /* En JPEG aussi, tel que livre : 207 Ko de jour, 124 de nuit. */
  poissy: '/illustrations/poissy-header.jpg',
};

/**
 * L'illustration de jour d'un serveur, s'il en a une.
 *
 * Les villes annoncees mais pas encore ouvertes n'en ont pas : elles
 * rendent `null`, et l'ecran qui les liste garde alors sa carte unie
 * plutot que d'afficher un cadre vide.
 */
export function illustrationDeJour(serverId: string): string | null {
  return HEADERS[serverId] ?? null;
}

/**
 * Les memes villes, de nuit.
 *
 * Tant qu'une ville n'a pas sa version nocturne, on retombe sur celle de
 * jour : mieux vaut une illustration a contretemps qu'un cadre vide. Le
 * repli joue aussi si le fichier est absent du dossier, d'ou le
 * `onError`.
 */
const HEADERS_NUIT: Record<string, string> = {
  marrakech: '/illustrations/marrakech-header-night.png',
  londres: '/illustrations/londres-header-night.png',
  barcelone: '/illustrations/barcelone-header-night.png',
  berlin: '/illustrations/berlin-header-night.png',
  paris: '/illustrations/paris-header-night.png',
  essec: '/illustrations/essec-header-night.jpg',
  poissy: '/illustrations/poissy-header-night.jpg',
};

/**
 * L'illustration nocturne d'un serveur, s'il en a une.
 *
 * Elle ne sert que pour le spot ou l'on se trouve deja : c'est le
 * fichier que son bandeau vient de charger, donc la carte parait a la
 * premiere image au lieu d'attendre un megaoctet, et elle montre la
 * ville dans la lumiere qu'on a sous les yeux.
 *
 * Les autres villes gardent leur illustration de jour, meme la nuit :
 * on ne les regarde pas pour savoir a quoi elles ressemblent ce soir,
 * on les regarde pour choisir ou aller — et une ville se choisit sur sa
 * carte postale.
 *
 * Le repli sur le jour joue tant qu'une ville n'a pas sa version
 * nocturne : mieux vaut une illustration a contretemps qu'un cadre vide.
 */
export function illustrationDeNuit(serverId: string): string | null {
  return HEADERS_NUIT[serverId] ?? HEADERS[serverId] ?? null;
}

/**
 * Ou couper, quand l'illustration n'a pas le format du bandeau.
 *
 * Aucune n'a le format du bandeau : toutes sont plus hautes, et doivent
 * donc etre cadrees — pas au milieu, sinon on garde du ciel en jetant la
 * ville. Chaque valeur vise ce qui identifie la ville.
 *
 * Une seule valeur sert au jour et a la nuit : basculer d'une lumiere a
 * l'autre ne doit pas deplacer le paysage. Cela suppose que les deux
 * illustrations d'une ville aient exactement les memes dimensions — c'est
 * la vraie contrainte, et elle se verifie a l'installation, l'outil les
 * affiche. Deux formats differents demanderaient deux nombres pour une
 * seule et meme vue, ce qui est une machinerie qu'on prefere eviter en
 * exportant les deux images au meme gabarit.
 */
const CADRAGE: Record<string, string> = {
  /*
    26 % : le minaret tient entier, pointe comprise, avec un peu de ciel
    au-dessus ; les montagnes se lisent derriere, et le porche et les
    palmiers remplissent le bas. Plus haut on ne gagne que du ciel et on
    perd le porche ; plus bas la pointe du minaret touche le bord, et
    c'est elle qui dit Marrakech.

    La meme valeur sert au jour et a la nuit — les deux illustrations font
    1424 par 749, comme l'exige la regle ci-dessus : la lumiere change,
    le paysage ne bouge pas d'un pixel.
  */
  marrakech: '50% 26%',
  /*
    34 % : la fleche de Big Ben passe tout juste, les batiments restent
    poses sur leur base et la Roue tient entiere. Plus haut, on gagnait le
    pont mais on coupait la fleche ; plus bas, on gagnait du ciel et on
    perdait le sol.
  */
  londres: '50% 34%',
  /*
    11 % : la Sagrada Familia tient entiere a droite, croix comprise et
    avec du ciel au-dessus, pendant que la mer, la ville et la verdure
    remplissent le bas. Plus haut on ne gagne que du ciel vide ; a 16 % la
    croix touche deja le bord, et au-dela le bandeau de titre que porte
    l'illustration finirait par entrer dans le cadre.
  */
  barcelone: '50% 11%',
  /*
    56 % : la sphere de la tour et son mat se lisent, et le Bode-Museum
    montre sa coupole et sa colonnade. La fenetre ne fait que 274 px sur
    1195, il faut choisir — plus haut on garde le mat entier et on perd le
    musee, plus bas le musee est entier et la tour touche deja le bord.
  */
  berlin: '50% 56%',
  /*
    65 % : la carte postale entiere — les immeubles, la coupole, la tour
    posee sur ses deux pieds et le pont avec ses arches. Plus haut on
    flottait dans les nuages ; plus bas on gagne les peniches mais on
    reperd la tour. Les deux images font 1370 par 768, jour et nuit.
  */
  paris: '50% 65%',
  /*
    20 %, et l'image agrandie (voir AGRANDISSEMENT).

    Le bandeau court sous la barre d'etat de l'iPhone, qui en mange les
    soixante premiers pixels, et l'ile noire tient le milieu : tout ce
    qui compte doit vivre dessous et a cote. A 44 % le logo passait
    derriere l'heure ; a 22 % il descend juste sous la barre, entier, en
    laissant la derniere ligne au-dessus de la courbe. Deux points plus
    bas, le « B » de BUSINESS passait dessous. Une seule valeur pour les
    deux lumieres, les images faisant toutes deux 1264 par 842.
  */
  essec: '50% 22%',
  /*
    36 % : le vieux pont et ses piles en entier au-dessus d'un ruban de
    Seine, et les fleches de la collegiale qui montent dans la barre
    d'etat a cote de l'heure, sans la toucher : leur pointe s'arrete vers
    38 px, l'heure finit vers 36. A 38 % la pointe venait buter contre
    l'heure ; plus bas on perd le reflet du pont. Une valeur pour les deux
    lumieres : 1264 par 843, jour et nuit.
  */
  poissy: '50% 36%',
};

/**
 * Les villes qui demandent a etre agrandies, et de combien.
 *
 * Le bandeau montre toute la largeur de l'illustration : la pousser de
 * cote est donc impossible sans l'agrandir d'abord. L'image est calee a
 * gauche, si bien que ce qu'on ajoute deborde a droite — tout le dessin
 * glisse dans ce sens, et le logo de l'ESSEC, colle au bord droit,
 * revient dans le cadre au lieu d'etre coupe.
 *
 * A n'utiliser que pour une illustration dont le sujet est excentre :
 * agrandir, c'est perdre du champ, et les villes ont le leur bien
 * centre.
 */
const AGRANDISSEMENT: Record<string, string> = {
  essec: '112%',
};

/**
 * Le rattrapage de la nuit, quand les deux photos ne sont pas prises du
 * meme point.
 *
 * La regle reste celle du haut du fichier : un seul cadrage pour les
 * deux lumieres, parce que basculer de l'une a l'autre ne doit pas
 * deplacer le paysage. Elle suppose deux vues identiques, ce qui est
 * vrai des villes — leurs illustrations sont la meme image repeinte.
 *
 * A l'ESSEC, ce sont deux photographies : la nocturne est prise un pas
 * plus a droite et un peu plus bas, et son logo tombait 39 px a droite
 * et 25 px au-dessus de celui du jour — mesure par correlation des
 * contours de l'ecusson, qui ne depend pas de la lumiere. Le cadrage
 * nocturne annule cet ecart, pour que le logo ne bouge pas d'un pixel
 * quand le soir tombe.
 *
 * `decalage` pousse l'image de cote : `objectPosition` n'y peut rien,
 * puisque le cadre montre deja toute la largeur — il ne reste que la
 * marge.
 */
const CADRAGE_NUIT: Record<string, { position: string; decalage: string }> = {
  essec: { position: '50% 17%', decalage: '-3.4%' },
};

export function ServerIllustration({
  name,
  ambiance = 'day',
}: {
  name: string;
  ambiance?: 'day' | 'night';
}) {
  const day = HEADERS[name];
  const wanted = (ambiance === 'night' ? HEADERS_NUIT[name] : undefined) ?? day;

  // Revenir au jour puis a la nuit doit reessayer l'image nocturne.
  const [source, setSource] = useState(wanted);
  const [manquante, setManquante] = useState(false);
  useEffect(() => {
    setSource(wanted);
    setManquante(false);
  }, [wanted]);

  /*
    L'autre lumiere, allee chercher d'avance.

    Le bandeau change de source quand le telephone passe du jour a la
    nuit. WebKit ne reclamait la nouvelle qu'au geste suivant : on
    gardait l'image d'avant sous une interface deja repeinte. Une image
    deja dans le cache se pose sans rien demander.
  */
  const autre = ambiance === 'night' ? day : HEADERS_NUIT[name];
  useEffect(() => {
    if (!autre) return;
    const image = new Image();
    image.src = autre;
  }, [autre]);

  /*
    Un aplat aux couleurs du spot quand il n'y a pas d'image.

    Deux cas y menent : une ville sans illustration declaree, et une ville
    qui en declare une que le dossier ne contient pas. Le second laissait
    l'icone d'image cassee du navigateur en haut de l'ecran — le repli ne
    savait que passer de la nuit au jour, pas renoncer.
  */
  if (!source || manquante) {
    return (
      <div
        aria-hidden="true"
        style={{
          width: '100%',
          height: '100%',
          background: 'linear-gradient(160deg, var(--s-primary), var(--s-primary-dark))',
        }}
      />
    );
  }

  return (
    <img
      src={source}
      alt=""
      aria-hidden="true"
      onError={() => {
        // Illustration nocturne pas encore deposee : on garde celle de
        // jour. Si c'est elle qui manque, il n'y a plus de recours.
        if (day && source !== day) setSource(day);
        else setManquante(true);
      }}
      /* Se repeindre d'un coup au changement de lumiere, sans garder
         l'image d'avant le temps de decoder la nouvelle. */
      decoding="sync"
      style={{
        width: AGRANDISSEMENT[name] ?? '100%',
        // Le rattrapage ne vaut que si c'est bien l'image nocturne qui
        // s'affiche : en repli sur celle du jour, il la decalerait.
        marginLeft: source === HEADERS_NUIT[name] ? CADRAGE_NUIT[name]?.decalage : undefined,
        /*
          `tokens.css` borne toutes les images a la largeur de leur
          parent — la regle qui evite qu'une photo de post deborde. Elle
          ramenait celle-ci a 100 % : l'agrandissement etait ecrit et ne
          se voyait nulle part, seul le cadrage vertical bougeait.
        */
        maxWidth: 'none',
        height: '100%',
        objectFit: 'cover',
        objectPosition:
          (source === HEADERS_NUIT[name] ? CADRAGE_NUIT[name]?.position : undefined) ??
          CADRAGE[name] ??
          '50% 50%',
        display: 'block',
      }}
    />
  );
}

export { SiroccoLogo, SiroccoMark } from './SiroccoLogo';
